import { Box, List, ListItem, Typography } from '@mui/material';

const SlateRenderer = ({ content, disabled = false }) => {
    let parsedContent = content;

    if (typeof content === 'string') {
        try {
            parsedContent = JSON.parse(content);
        } catch (e) {
            return <Typography variant="body1">{content}</Typography>;
        }
    }

    if (!Array.isArray(parsedContent) || parsedContent.length === 0) {
        return null;
    }

    const renderNode = (node, index) => {
        if (node.text !== undefined) {
            let text = node.text;

            if (node.bold) {
                text = <Box component="span" key={index} sx={{ fontWeight: 'bold' }}>{text}</Box>;
            }
            if (node.italic) {
                text = <Box component="span" key={index} sx={{ fontStyle: 'italic' }}>{text}</Box>;
            }
            if (node.underline) {
                text = <Box component="span" key={index} sx={{ textDecoration: 'underline' }}>{text}</Box>;
            }
            if (node.code) {
                text = (
                    <Box
                        component="code"
                        key={index}
                        sx={{
                            backgroundColor: 'action.hover',
                            padding: '2px 4px',
                            borderRadius: '4px',
                            fontFamily: 'monospace',
                            fontSize: '0.875em'
                        }}
                    >
                        {text}
                    </Box>
                );
            }

            return text;
        }

        if (node.children) {
            const children = node.children.map((child, childIndex) =>
                renderNode(child, `${index}-${childIndex}`)
            );

            const sx = {};
            if (node.align) {
                sx.textAlign = node.align;
            }

            switch (node.type) {
                case 'heading-one':
                    return (
                        <Typography key={index} variant="h4" component="h1" sx={sx} gutterBottom>
                            {children}
                        </Typography>
                    );
                case 'heading-two':
                    return (
                        <Typography key={index} variant="h5" component="h2" sx={sx} gutterBottom>
                            {children}
                        </Typography>
                    );
                case 'bulleted-list':
                    return (
                        <List key={index} sx={{ listStyleType: 'disc', pl: 2, ...sx, fontSize: '1rem' }}>
                            {children}
                        </List>
                    );
                case 'numbered-list':
                    return (
                        <List key={index} component="ol" sx={{ listStyleType: 'decimal', pl: 2, ...sx, fontSize: '1rem' }}>
                            {children}
                        </List>
                    );
                case 'list-item':
                    return (
                        <ListItem key={index} sx={{ display: 'list-item', padding: 0, ...sx }}>
                            {children}
                        </ListItem>
                    );
                case 'link':
                    return (
                        <Typography
                            key={index}
                            component="a"
                            href={disabled ? undefined : node.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                                if (disabled) {
                                    e.preventDefault();
                                }
                            }}
                            sx={{
                                color: disabled ? 'text.secondary' : 'primary.main',
                                textDecoration: disabled ? 'none' : 'underline',
                                cursor: disabled ? 'default' : 'pointer',
                                pointerEvents: disabled ? 'none' : 'auto'
                            }}
                        >
                            {children}
                        </Typography>
                    );
                case 'paragraph':
                default:
                    return (
                        <Typography key={index} variant="body1" component="p" sx={{ mb: 0.1, ...sx }}>
                            {children}
                        </Typography>
                    );
            }
        }

        return null;
    };

    return (
        <Box sx={{
            overflowWrap: 'anywhere',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
        }}>
            {parsedContent.map((node, index) => renderNode(node, index))}
        </Box>
    );
};

export default SlateRenderer;
